import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '@/store/slices/UserSlice'
import { setBrands , setSelectedBrandId } from '@/store/slices/BrandSlice'
import { resetAllTokenErrors } from '@/store/slices/TokenSllice'
import { useBrandRefresh } from '@/hooks/useBrandRefresh';
import { RootState } from '@/store';

const GoogleCallback = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const dispatch = useDispatch();
    const { refreshBrands } = useBrandRefresh();
    const baseURL = import.meta.env.PROD
        ? import.meta.env.VITE_API_URL
        : import.meta.env.VITE_LOCAL_API_URL;

    const selectedBrandId = useSelector((state: RootState) => state.brand.selectedBrandId);

    useEffect(() => {
        const handleOauthCallback = async () => {
            try {
                const queryParams = new URLSearchParams(window.location.search);
                const googletoken = queryParams.get('token');
                const fbToken = queryParams.get('fbToken');
                const igToken = queryParams.get('igToken');
                const googleAdRefreshToken = queryParams.get('googleadRefreshToken');
                const googleAnalyticsRefreshToken = queryParams.get('googleanalyticsRefreshToken');
                const zohoRefreshToken = queryParams.get('zohoToken');
                const userId = queryParams.get('userId');
                const appToken = queryParams.get('shopify_token');
               

                // Helper function for token update
                const updateToken = async (url: string, token: string, type: string) => {
                    try {
                        const sourcePage = queryParams.get('source') || '/dashboard';
                        const sourceUrl = new URL(sourcePage, window.location.origin);
                        const brandIdFromSource = sourceUrl.searchParams.get('reconnectBrand') || selectedBrandId;

                        if (!brandIdFromSource) {
                            toast({
                                title: 'Update Failed',
                                description: 'No brand selected for token update. Please try again from the brand profile.',
                                variant: 'destructive',
                            });
                            navigate('/profile');
                            return false;
                        }

                        dispatch(setSelectedBrandId(brandIdFromSource));

                        // If we have a brandId, proceed with normal token update
                        const response = await axios.put(
                            `${baseURL}${url}/${type}?brandId=${brandIdFromSource}`,
                            {}, // Empty body since we are using query params
                            { 
                                params: { [type]: token },
                                withCredentials: true 
                            }
                        );                        
                        if (response.data.success) {
                            console.log(`${type} token updated successfully`);
                            // Reset all token errors when a token is successfully updated
                            dispatch(resetAllTokenErrors());
                            
                            if (type === "zohoToken") {
                                console.log("Zoho token updated, redirecting to /profile");
                                navigate('/profile');
                            } else {
                                let modalToOpen = '';
                                
                                // Determine which modal to open based on token type
                                switch(type) {
                                    case 'googleadRefreshToken':
                                        modalToOpen = 'googleAds';
                                        break;
                                    case 'googleanalyticsRefreshToken':
                                        modalToOpen = 'googleAnalytics';
                                        break;
                                    case 'fbToken':
                                        modalToOpen = 'facebook';
                                        break;
                                    case 'igToken':
                                        modalToOpen = 'instagram';
                                        break;
                                }
                                
                                // Show success toast
                                toast({
                                    title: 'Success',
                                    description: `${type} token updated successfully.`,
                                    variant: 'default',
                                });
                                
                                // Preserve reconnectBrand (and other) query params from source
                                if (modalToOpen) {
                                    sourceUrl.searchParams.set('openModal', modalToOpen);
                                    if (!sourceUrl.searchParams.get('reconnectBrand')) {
                                        sourceUrl.searchParams.set('reconnectBrand', brandIdFromSource);
                                    }
                                    navigate(`${sourceUrl.pathname}${sourceUrl.search}`);
                                } else {
                                    navigate(sourceUrl.pathname + sourceUrl.search);
                                }
                                return true;
                            }
                            return true;
                        } else {
                            console.error(`Failed to update ${type} token`);
                            toast({
                                title: 'Update Failed',
                                description: `Unable to update ${type} token.`,
                                variant: 'destructive',
                            });
                            navigate('/dashboard');
                            return false;
                        }
                    } catch (error: any) {
                        console.error(`Error updating ${type} token:`, error);
                        toast({
                            title: 'Update Failed',
                            description: error?.response?.data?.message || `Unable to update ${type} token.`,
                            variant: 'destructive',
                        });
                        navigate('/');
                        return false;
                    }
                };

                if (googletoken) {
                    console.log('Token found, proceeding with login');
                    const login = await axios.post(`${baseURL}/api/auth/login/oauth?auth_token=${googletoken}`, { email: null, password: null });

                    if (login.data.success) {
                        console.log('Login successful');
                        const user = login.data.user;

                        dispatch(setUser(user));

                        // For Google/Normal login users, check if they have brands
                        if (!user.brands || user.brands.length === 0) {
                            console.log('No brands found, redirecting to brand setup');
                            navigate('/brand-setup');
                        } else {
                            console.log('Brands found, redirecting to dashboard');
                            navigate('/dashboard');
                        }
                        return;
                    } else {
                        console.log('Login failed, redirecting to /');
                        toast({
                            title: 'Login Failed',
                            description: 'Unable to authenticate with Google.',
                            variant: 'destructive',
                        });
                        navigate('/');
                        return;
                    }
                }


                if (fbToken) {
                    await updateToken('/api/auth/updateTokens', fbToken, 'fbToken');
                    return;
                }

                if (igToken) {
                    await updateToken('/api/auth/updateTokens', igToken, 'igToken');
                    return;
                }

                if (googleAdRefreshToken) {
                    await updateToken('/api/auth/updateTokens', googleAdRefreshToken, 'googleadRefreshToken');
                    return;
                }

                if (googleAnalyticsRefreshToken) {
                    await updateToken('/api/auth/updateTokens', googleAnalyticsRefreshToken, 'googleanalyticsRefreshToken');
                    return;
                }

                if (zohoRefreshToken) {
                    await updateToken('/api/auth/updateTokens/zoho', zohoRefreshToken, 'zohoToken');
                    return;
                }

                if (userId && appToken) {
                    console.log('Token found, proceeding with user saving');
                    const getUser = await axios.get(`${baseURL}/api/users/getuser/${userId}?token=${appToken}`);

                    if (getUser.data.success) {
                        const user = getUser.data.user;
                        const brands = getUser.data.brands;
                        const sortedBrands = [...brands].sort((a, b) =>
                            a.name.replace(/_/g, " ").localeCompare(b.name.replace(/_/g, " "), undefined, { sensitivity: "base" })
                        );

                        dispatch(setUser(user));
                        dispatch(setBrands(sortedBrands));
                        if (sortedBrands.length > 0) {
                            dispatch(setSelectedBrandId(sortedBrands[0]._id));
                        }
                        await refreshBrands();  
                        
                        // For Shopify login users, check if they have brands
                        if (!user.brands || user.brands.length === 0) {
                            console.log('No brands found, redirecting to brand setup');
                            navigate('/brand-setup');
                        } else {
                            console.log('Brands found, redirecting to dashboard');
                            navigate('/dashboard');
                        }
                        return;
                    } else {
                        console.log('Login failed, redirecting to /login');
                        toast({
                            title: 'Login Failed',
                            description: 'Unable to authenticate with Google.',
                            variant: 'destructive',
                        });
                        navigate('/login');
                        return;
                    }
                }

                console.log('No token present, redirecting to /login');
                navigate('/login');

            } catch (error) {
                console.error('Error during OAuth callback:', error);
                toast({
                    title: 'Error',
                    description: 'An error occurred while handling the login callback.',
                    variant: 'destructive',
                });
                navigate('/login');
            }
        };

        handleOauthCallback();
    }, [navigate, setUser, toast, baseURL, selectedBrandId]);


    return null;
};

export default GoogleCallback;